import React from 'react';
import { StyleSheet, Text, View, Dimensions, TouchableOpacity, Platform, Alert } from 'react-native';
import { NativeStackScreenProps } from '@react-navigation/native-stack';
import { RootStackParamList } from '../../App';
import { SafeAreaView } from 'react-native-safe-area-context';
import { useGame } from '../hooks/useGame';
import { useNavigation } from '@react-navigation/native';
import { Ionicons } from '@expo/vector-icons';
import * as Sharing from 'expo-sharing';

type Props = NativeStackScreenProps<RootStackParamList, 'Display'>;

export default function DisplayScreen({ route }: Props) {
  const { gameId } = route.params;
  const { game } = useGame(gameId);
  const navigation = useNavigation();

  const handleCast = () => {
    if (Platform.OS === 'web') {
      Alert.alert(
        'Cast to TV',
        'To cast this screen to your TV:\n\n' +
        '1. Click the Chrome menu (⋮)\n' +
        '2. Select "Cast..."\n' +
        '3. Choose your TV or casting device\n\n' +
        'Tip: You can also right-click anywhere and select "Cast..." or use the keyboard shortcut (Alt+Shift+C)',
        [{ text: 'OK' }]
      );
    } else if (Platform.OS === 'ios') {
      // On iOS mobile, use AirPlay
      Sharing.shareAsync('', {
        dialogTitle: 'Cast to AirPlay',
        UTI: 'public.screen-recording'
      });
    } else {
      // On Android mobile
      Alert.alert(
        'Cast to TV',
        'To cast this screen to your TV, please use your device\'s built-in casting feature from the control center/quick settings.',
        [{ text: 'OK' }]
      );
    }
  };

  const renderHeader = () => (
    <View style={styles.header}>
      <TouchableOpacity
        style={styles.closeButton}
        onPress={() => navigation.goBack()}
      >
        <Ionicons name="close" size={24} color="#1E4B8F" />
      </TouchableOpacity>

      <View style={styles.wordBankIndicator}>
        <Text style={styles.wordBankName}>
          {game?.state.wordBankId === 'jamaican' ? 'Jamaican Words' :
           game?.state.wordBankId === 'tech' ? 'Tech Jargon' :
           game?.state.wordBankId === 'bajan' ? 'Bajan Dialect' : 'Select Word Bank'}
        </Text>
      </View>

      <TouchableOpacity
        style={styles.castButton}
        onPress={handleCast}
      >
        <Ionicons name="tv-outline" size={24} color="#1E4B8F" />
        <Text style={styles.castButtonText}>
          {Platform.OS === 'web' ? 'How to Cast' : 'Cast to TV'}
        </Text>
      </TouchableOpacity>
    </View>
  );

  const renderCurrentWord = () => {
    if (!game?.currentRound?.word) {
      return (
        <View style={styles.waitingContainer}>
          <Text style={styles.waitingText}>Waiting for the next word...</Text>
        </View>
      );
    }

    return (
      <View style={styles.wordContainer}>
        <Text style={styles.wordLabel}>CURRENT WORD</Text>
        <Text style={styles.word}>{game.currentRound.word}</Text>
      </View>
    );
  };

  const renderDefinitions = () => {
    if (!game?.currentRound?.definitions?.length) {
      return (
        <View style={styles.waitingContainer}>
          <Text style={styles.waitingText}>Players are writing definitions...</Text>
          {game?.state.players && (
            <Text style={styles.submissionStatus}>
              {game.currentRound?.definitions?.length || 0} of {game.state.players.filter(p => !p.isReader).length} players have submitted
            </Text>
          )}
        </View>
      );
    }

    // Only show definitions if all non-reader players have submitted
    const nonReaderCount = game.state.players.filter(p => !p.isReader).length;
    const submittedCount = game.currentRound.definitions.length;

    if (submittedCount < nonReaderCount) {
      return (
        <View style={styles.waitingContainer}>
          <Text style={styles.waitingText}>Waiting for all players to submit...</Text>
          <Text style={styles.submissionStatus}>
            {submittedCount} of {nonReaderCount} players have submitted
          </Text>
        </View>
      );
    }

    return (
      <View style={styles.definitionsContainer}>
        <Text style={styles.definitionsLabel}>DEFINITIONS</Text>
        {game.currentRound.definitions.map((def, index) => (
          <View key={index} style={styles.definitionCard}>
            <Text style={styles.definitionNumber}>Definition {index + 1}</Text>
            <Text style={styles.definitionText}>{def.text}</Text>
            {def.votes?.length > 0 && (
              <Text style={styles.votesText}>Votes: {def.votes.length}</Text>
            )}
          </View>
        ))}
      </View>
    );
  };

  return (
    <View style={styles.container}>
      {renderHeader()}
      <View style={styles.content}>
        {renderCurrentWord()}
        {renderDefinitions()}
      </View>
    </View>
  );
}

const { width, height } = Dimensions.get('window');

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#FFD84D',
  },
  header: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: 20,
    paddingTop: Platform.OS === 'ios' ? 60 : 20,
  },
  closeButton: {
    padding: 8,
    borderRadius: 8,
    backgroundColor: 'rgba(255, 255, 255, 0.8)',
  },
  castButton: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: 8,
    borderRadius: 8,
    backgroundColor: 'rgba(255, 255, 255, 0.8)',
    gap: 8,
  },
  castButtonText: {
    color: '#1E4B8F',
    fontSize: 16,
    fontWeight: 'bold',
  },
  content: {
    flex: 1,
    padding: 40,
    justifyContent: 'center',
  },
  waitingContainer: {
    alignItems: 'center',
    justifyContent: 'center',
    padding: 20,
  },
  waitingText: {
    fontSize: 32,
    color: '#1E4B8F',
    textAlign: 'center',
    marginBottom: 20,
  },
  submissionStatus: {
    fontSize: 24,
    color: '#E63946',
    textAlign: 'center',
    fontWeight: 'bold',
  },
  wordContainer: {
    alignItems: 'center',
    marginBottom: 60,
  },
  wordLabel: {
    fontSize: 24,
    color: '#1E4B8F',
    fontWeight: 'bold',
    marginBottom: 20,
    letterSpacing: 2,
  },
  word: {
    fontSize: 64,
    color: '#E63946',
    fontWeight: 'bold',
    textAlign: 'center',
  },
  definitionsContainer: {
    alignItems: 'center',
  },
  definitionsLabel: {
    fontSize: 24,
    color: '#1E4B8F',
    fontWeight: 'bold',
    marginBottom: 20,
    letterSpacing: 2,
  },
  definitionCard: {
    backgroundColor: '#fff',
    padding: 20,
    borderRadius: 12,
    marginBottom: 20,
    width: width * 0.8,
    maxWidth: 600,
    shadowColor: '#1E4B8F',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 3,
  },
  definitionNumber: {
    fontSize: 20,
    color: '#E63946',
    fontWeight: 'bold',
    marginBottom: 10,
  },
  definitionText: {
    fontSize: 28,
    color: '#1E4B8F',
    textAlign: 'center',
    lineHeight: 36,
  },
  votesText: {
    fontSize: 20,
    color: '#E63946',
    textAlign: 'center',
    marginTop: 10,
    fontWeight: 'bold',
  },
  wordBankIndicator: {
    backgroundColor: '#1E4B8F',
    padding: 8,
    borderRadius: 8,
    alignItems: 'center',
    flex: 1,
    marginHorizontal: 10,
  },
  wordBankName: {
    color: '#fff',
    fontSize: 16,
    fontWeight: 'bold',
    marginBottom: 2,
  },
  wordBankDetails: {
    color: '#fff',
    fontSize: 12,
    opacity: 0.8,
  },
}); 